import { MapPin, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const MA = [
  "Newburyport", "Beverly", "Andover", "Lexington", "Concord", "Salem",
  "Gloucester", "Haverhill", "Amesbury", "Ipswich", "Marblehead", "Danvers",
];
const NH = [
  "Portsmouth", "Dover", "Exeter", "Nashua", "Hampton", "Durham",
  "Stratham", "Newmarket", "Rye", "Hampton Falls", "Greenland", "North Hampton",
];

const StateCard = ({ state, abbr, towns, accent }) => (
  <div className="group relative overflow-hidden rounded-[2rem] bg-white/70 p-7 ring-1 ring-charcoal/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_70px_-40px_rgba(76,0,140,0.45)] sm:p-9">
    <div className={`pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full blur-3xl transition-opacity duration-500 ${accent ? "bg-violet/15" : "bg-sage/25"} opacity-60 group-hover:opacity-100`} />
    <div className="relative flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${accent ? "bg-lavender text-violet" : "bg-sage/50 text-charcoal"}`}>
          <MapPin size={20} />
        </span>
        <div>
          <h3 className="font-display text-2xl leading-none text-charcoal">{state}</h3>
          <p className="mt-1 text-sm text-charcoal/45">{towns.length} towns served</p>
        </div>
      </div>
      <span className="font-display text-4xl tracking-tight text-charcoal/10">{abbr}</span>
    </div>
    <div className="relative mt-6 flex flex-wrap gap-2">
      {towns.map((t) => (
        <a
          key={t}
          href={`/junk-removal/${t.toLowerCase().replace(/ /g, "-")}-${abbr.toLowerCase()}`}
          className="rounded-full bg-canvas/80 px-3.5 py-1.5 text-sm font-medium text-charcoal/70 ring-1 ring-charcoal/5 transition-colors duration-300 hover:bg-lavender hover:text-violet hover:ring-violet/20"
        >
          {t}
        </a>
      ))}
    </div>
  </div>
);

export default function ServiceAreas({ onStart }) {
  const total = MA.length + NH.length;
  return (
    <section id="areas" className="py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet/70">Service areas</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display text-4xl leading-[1.02] tracking-tight text-charcoal sm:text-5xl text-balance">
                Now reclaiming homes across the Seacoast.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg leading-relaxed text-charcoal/60">
                We proudly serve Massachusetts and New Hampshire — and we're growing fast.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-8 flex items-center gap-6 rounded-[1.6rem] bg-white/60 px-6 py-5 ring-1 ring-charcoal/5">
                <div>
                  <p className="font-display text-4xl tracking-tight text-violet">{total}+</p>
                  <p className="text-sm text-charcoal/50">towns covered</p>
                </div>
                <span className="h-10 w-px bg-charcoal/10" />
                <div>
                  <p className="font-display text-4xl tracking-tight text-charcoal">2</p>
                  <p className="text-sm text-charcoal/50">states &amp; counting</p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <button
                onClick={onStart}
                data-testid="areas-check-cta"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-charcoal px-7 py-3.5 text-sm font-semibold text-canvas transition-all duration-300 hover:bg-violet hover:-translate-y-0.5"
              >
                Don't see your town? Check coverage
                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </Reveal>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
            <Reveal delay={0.1}>
              <StateCard state="Massachusetts" abbr="MA" towns={MA} accent />
            </Reveal>
            <Reveal delay={0.18}>
              <StateCard state="New Hampshire" abbr="NH" towns={NH} />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
